import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { PaymentMethods } from '@/account/Interfaces';
import { PaymentsSelectionModal } from '@/account/PaymentsSelectionModal';
import { TosModal } from '@/account/TosModal/TosModal';
import { RadioPaymentsModal, UpgradeFailureModal } from '@/account/index';
import {
  filterActivePaymentMethod,
  useGetPaymentMethod,
} from '@/account/paymentMethodsService';
import { PaymentsFeedback } from '@/account/payments/paymentsUpdateModal/PaymentsComponents/PaymentsFeedback';
import { useGetPaymentType } from '@/account/payments/paymentsUpdateModal/PaymentsComponents/constants';
import {
  useProcessBraintree,
  useProcessCard,
  useProcessPayPal,
} from '@/account/payments/useSubscriptionUpgrade';
import { useAccount } from '@/account/useAccount';
import { useSession } from '@/auth/Session';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { ProcessingSpinnerModal } from '@/reports/shared/PdfAddon/ProcessingSpinnerModal';
import { useSubmitLegal } from '@/reports/shared/PdfAddon/useTosService';
import { PremiumPaymentModal } from '@/reports/shared/PremiumPaymentModal/PremiumPaymentModal';

export interface PremiumPaymentCardProps {
  index: number;
  setIndex: Dispatch<SetStateAction<number>>;
  openSuccessModal?: () => void;
}

enum State {
  None,
  SelectPayment,
  AddPayment,
  ProcessingPayment,
  Success,
  Error,
}

export const PremiumPaymentCard = ({
  index,
  setIndex,
  openSuccessModal,
}: PremiumPaymentCardProps) => {
  const { paymentPremiumModalCardData } = useAppConstantsContext();
  const { data: account, refetch } = useAccount(true);
  const paymentMethodQuery = useGetPaymentMethod();
  const accountInfo = account?.account;
  const { logError } = useAppConfigContext();
  const { setSession } = useSession();

  const closeModals = () => setIndex(0);
  const nextModal = () => {
    setIndex((prevState) => prevState + 1);
  };

  const [currentPlanPrice, setCurrentPlanPrice] = useState('');
  const [offerPlanPrice] = useState(
    paymentPremiumModalCardData?.premiumPlanAmount || ''
  );
  const [offerPlanName] = useState(
    paymentPremiumModalCardData?.premiumPlan || ''
  );
  const [differencePlanPrice, setDifferencePlanPrice] = useState('');
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [processingPayment, setProcessingPayment] = useState<boolean>(false);

  const [upgradeErrors, setUpgradeErrors] = useState<string[]>([]);
  const [hadPaymentErrors, setHadPaymentErrors] = useState(false);
  const [upgradeState, setUpgradeState] = useState(State.None);
  const isState = (state: State) => upgradeState === state;

  const [activePayment, setActivePayment] = useState<PaymentMethods>();

  useEffect(() => {
    if (!paymentMethodQuery.isLoading && !paymentMethodQuery.isError) {
      setActivePayment(filterActivePaymentMethod(paymentMethodQuery?.data));
    }
  }, [paymentMethodQuery]);

  useEffect(() => {
    if (accountInfo) {
      setCurrentPlanPrice(accountInfo.subscription_info.money_amount);
      const offerPrice = parseFloat(offerPlanPrice.replace('$', ''));
      const currentPrice = parseFloat(currentPlanPrice.replace('$', ''));
      setDifferencePlanPrice(`$${(offerPrice - currentPrice).toFixed(2)}`);
    }
  }, [accountInfo, currentPlanPrice, offerPlanPrice]);

  const updateLegalQuery = useSubmitLegal();

  const paymentProcessCardQuery = useProcessCard();
  const paymentProcessPayPalQuery = useProcessPayPal();
  const paymentProcessBraintreeQuery = useProcessBraintree();

  const upgradePlan = () => {
    if (!activePayment) return;

    const paymentType = useGetPaymentType(activePayment);

    interface MutationFunctions {
      paypal: typeof paymentProcessPayPalQuery;
      braintree: typeof paymentProcessBraintreeQuery;
      card: typeof paymentProcessCardQuery;
    }

    const mutationFunctions: MutationFunctions = {
      paypal: paymentProcessPayPalQuery,
      braintree: paymentProcessBraintreeQuery,
      card: paymentProcessCardQuery,
    };

    const mutationFunction =
      mutationFunctions[paymentType] || paymentProcessCardQuery;

    const mutationParams = {
      plan: { unique_key: offerPlanName },
      ...((paymentType === 'paypal' && {
        paypalId: activePayment.id,
      }) as { paypalId: number }),
      ...((paymentType === 'card' && {
        cardId: activePayment.id,
      }) as {
        cardId: number;
      }),
      ...((paymentType === 'braintree' && {
        braintreeId: activePayment.id,
      }) as { braintreeId: number }),
      source_of_payment: 'manual_upgrade_attempt',
    };

    setProcessingPayment(false);

    mutationFunction.mutate(mutationParams, {
      async onSuccess() {
        const { data } = await refetch();
        setUpgradeState(State.Success);
        setSession({
          account: data,
        });
        if (openSuccessModal) {
          openSuccessModal();
        }
      },
      async onError(error: unknown) {
        logError(
          `Premium Data Upgrade error with ${paymentType} payment method.`,
          error as Error
        );
        let cause = (error as Error).cause as string[];
        const stringifiedErrors = cause?.join(', ');
        if (stringifiedErrors === 'Unknown Checkout Failure Reason') {
          cause = [
            'Your payment failed, please try again or choose a different payment method.',
          ];
        }
        setUpgradeErrors(cause);
        setUpgradeState(State.Error);
        closeModals();
      },
    });
  };

  const updateLegalHandler = () => {
    setProcessingPayment(true);
    updateLegalQuery.mutate(undefined, {
      onSuccess() {
        upgradePlan();
        nextModal();
      },
      onError() {
        setUpgradeState(State.Error);
        closeModals();
      },
    });
  };

  return (
    <>
      <PremiumPaymentModal
        isOpen={index === 1}
        onClose={closeModals}
        editBilling={() => setUpgradeState(State.SelectPayment)}
        proceedToNextStep={nextModal}
        currentPlanAmount={currentPlanPrice}
        premiumPlanAmount={offerPlanPrice}
        differencePlanAmount={differencePlanPrice}
        currentPlanReports={
          paymentPremiumModalCardData?.currentPlanReports || []
        }
        premiumPlanReports={
          paymentPremiumModalCardData?.premiumPlanReports || []
        }
        premiumMessage={paymentPremiumModalCardData?.premiumMessage || ''}
        packagePrice={paymentPremiumModalCardData?.packagePrice || '$48.79'}
        buttonColor={paymentPremiumModalCardData?.buttonColor || 'success'}
      />
      <TosModal
        open={index === 2}
        onClose={closeModals}
        acceptTos={updateLegalHandler}
        showCheckbox
        billingFrecuency="monthly"
        billedPrice={paymentPremiumModalCardData?.premiumPlanAmount || ''}
        cta={{ text: 'Accept and Upgrade', extraWide: true, processingPayment }}
        showPaymentMethod={false}
      />

      <ProcessingSpinnerModal open={index === 3} />

      <RadioPaymentsModal
        isOpen={isState(State.AddPayment)}
        onCloseHandle={() => setUpgradeState(State.SelectPayment)}
      />
      <PaymentsFeedback
        message="Success! You now have access to Premium Data!"
        severity="success"
        showSnackBar={showSuccessAlert}
        setShowSnackBar={setShowSuccessAlert}
      />
      <UpgradeFailureModal
        open={isState(State.Error)}
        errors={upgradeErrors}
        onTryAgain={() => upgradePlan()}
        onChangeCard={() => {
          setHadPaymentErrors(true);
          setUpgradeState(State.SelectPayment);
        }}
        onClose={() => {
          setUpgradeState(State.None);
        }}
      />
      <PaymentsSelectionModal
        open={isState(State.SelectPayment)}
        cta="Use This Payment Method"
        onAddPaymentMethod={() => setUpgradeState(State.AddPayment)}
        onSelectedPaymentMethod={() => {
          if (hadPaymentErrors) {
            setHadPaymentErrors(false);
            upgradePlan();
          } else {
            setUpgradeState(State.None);
          }
        }}
        onClose={() => {
          if (hadPaymentErrors) {
            setHadPaymentErrors(false);
            setUpgradeState(State.Error);
          } else {
            setUpgradeState(State.None);
          }
        }}
      />
    </>
  );
};
